import type { LegacyIntegrationCategory } from './legacy-category';

/** Live per-vendor health for the dedicated provider-status page (D). Mirrors the
 *  operator-bff `IProvidersStatus` wire shape. */
export type ProviderStatusKind = 'HEALTHY' | 'THROTTLED' | 'DEGRADED' | 'IDLE';

export interface ProviderStatus {
  vendor: string;
  status: ProviderStatusKind;
  lastOutcome?: 'ok' | 'throttled' | 'error';
  lastSeenAt?: string;
  lastRetryAfterMs?: number;
  lastLatencyMs?: number;
  /** Recent-window activity — "current requests / rate limits". */
  requestCount?: number;
  throttleCount?: number;
  errorCount?: number;
  avgLatencyMs?: number;
  p95LatencyMs?: number;
  /** Real vendor-egress rate-limit picture (configured limit + live GCRA saturation
   *  + breaker). Present for providers backed by a real vendor (hotel, wallet). */
  rateLimit?: {
    configured?: string;
    realVendors?: string[];
    saturatedForMs?: number;
    breakerState?: string;
  };
}

export interface ProvidersStatus {
  providers: ProviderStatus[];
  /** Vendors currently/recently rate-limited. */
  throttled: string[];
  computedAt: string;
}

/** Flight-data (FlightAware/AeroAPI via the flight predictor) card state.
 *  Mirrors the operator-bff `IFlightDataStatus` wire shape. */
export interface FlightDataStatus {
  configured: boolean;
  reachable: boolean;
  /** Tenant carrier the refresh pulls for (ICAO). */
  icao: string;
  mode?: string;
  dailyPageBudget?: number;
  pagesUsedToday?: number;
  pagesLeft?: number;
  minIntervalHours?: number;
  lastPull?: {
    status: string;
    startedAt: string;
    finishedAt?: string;
    pages?: number;
    flightsUpserted?: number;
    error?: string;
  };
  computedAt: string;
}

/** One per-ICAO outcome of the forced refresh (a refused pull is `skipped` +
 *  `reason` — still a 200, never a thrown error). */
export interface FlightDataRefreshOutcome {
  icao: string;
  action: 'pulled' | 'skipped';
  reason?: string;
  status?: 'ok' | 'error';
  pages?: number;
  rowsFetched?: number;
  flightsUpserted?: number;
  durationMs?: number;
  error?: string;
  lastOkAt?: string;
}

/** Mirrors the operator-bff `IFlightDataRefreshResult` wire shape. */
export interface FlightDataRefreshResult {
  icao: string;
  pulled: boolean;
  outcomes: FlightDataRefreshOutcome[];
  pagesUsed: number;
  pagesLeft?: number;
  computedAt: string;
}

/* ─────────────────── Connected integrations (slice E2) ────────────────────
 * Mirrors the FROZEN seam `operator-bff:GET /v1/providers/integrations@v1`,
 * whose fields are `@nexa/integrations-contract`'s `IIntegrationDescriptor` /
 * `IUnknownDeclarant`. operator-ui is not a root-workspace member and cannot
 * import that package, so the shapes are mirrored here — additively and by
 * name, so a drift shows up as a type error the moment the wire changes.
 *
 * SECURITY: the descriptor carries `credentialsPresent: boolean` and a
 * `baseUrl` the declaring app already redacted (userinfo + query stripped). No
 * field on this type may ever hold credential material, and the card renders
 * ONLY the typed fields below — never the raw payload.
 */

/** `integrations-tds.md` §2.1 — the SERVICES, mirrored from the contract's literal
 *  union (`service-categories@v2`, providers-by-service M2). */
export type ServiceCategory =
  | 'hotel'
  | 'wallet'
  | 'notifications'
  | 'transport'
  | 'flight-info'
  | 'passenger-data'
  | 'air-ticketing'
  | 'ai'
  | 'observability';

/** What a declarant may send: a service. Any LEGACY spelling read as one would
 *  come from `legacy-category.ts`; since P4 (`service-categories@v3`) there is none. */
export type IntegrationCategory = ServiceCategory | LegacyIntegrationCategory;

export type AuthStrategy = 'oauth2' | 'hmac' | 'apiKey' | 'none';

/**
 * How the integration is wired RIGHT NOW. `mock` is a first-class, fully
 * configured state — an instance running on mocks is legitimately configured,
 * it just has to be UNMISTAKABLE on screen. There is deliberately no
 * `'unknown'` member: not knowing is a fact about a DECLARANT, never a mode
 * (see {@link UnknownDeclarant}).
 */
export type IntegrationMode = 'mock' | 'sandbox' | 'production';

/** One integration, as declared by the app that actually built the adapter. */
export interface IntegrationDescriptor {
  vendor: string;
  category: IntegrationCategory;
  /** What the declaring app uses it for ('search' | 'book' | 'send' | …).
   *  Absent means "the declarant did not say", not "nothing". */
  capabilities?: string[];
  enabled: boolean;
  mode: IntegrationMode;
  /** Host (+ path) only, already redacted upstream. */
  baseUrl?: string;
  authStrategy: AuthStrategy;
  /** Whether the declaring process holds the credential. NEVER the value. */
  credentialsPresent: boolean;
  timeoutMs?: number;
  source: 'env';
  /** The service holding the adapter (`nexa-booking`) — where the probe runs. */
  declaredBy: string;
  /** #839 — the declaring app reads the operator switch on every request. */
  toggleable?: boolean;
  /** #839 — the operator switch for this row (absent from an older BFF). */
  toggle?: IntegrationToggle;
  /**
   * M2 (D14/D15) — the modes the declaring app BUILT and routes to per call.
   * Absent, [] or one element = fixed at boot: the screen shows «fijo».
   * `mode` above stays the BOOT mode.
   */
  selectableModes?: IntegrationMode[];
  /**
   * 2026-10-29 (`defaultCapabilities@v1`) — for a provider that offers several capabilities of one
   * service (Uber: trips and vouchers), the ones in force with nobody having decided; each
   * capability's «Orden de uso» says the rest. Absent = every capability.
   */
  defaultCapabilities?: string[];
  /** M2 — the mode the declarant uses NOW (§6 invariant 12). Absent from an older BFF → `mode`. */
  effectiveMode?: IntegrationMode;
  /** M2 — the operator's mode choice in force: who, when, why, and whether it is ignored. */
  modeOverride?: IntegrationModeOverride;
  /**
   * M3 (I3, `integrations-tds` §15.7) — the declaring app BOOTED (`mode`) in a mode
   * this deployment kind does not allow. Since M3 every service refuses such a boot,
   * so only an older build can report one. Information only: `mode` and
   * `effectiveMode` stay what the app really uses and are never relabelled. Absent
   * when the boot mode is allowed, never `false`. B3 lets it through the BFF.
   */
  bootModeForbidden?: true;
  /**
   * ADDITIVE (#840) — availability computed by the operator-bff on the read
   * (probe cache, live traffic, breaker), NOT part of the declaring app's
   * descriptor. Optional: an older BFF omits it and the card falls back to
   * what configuration alone can say (`fallbackHealth`).
   */
  health?: IntegrationHealth;
}

/**
 * The availability states of one integration (#840). Mirrors the operator-bff
 * `IIntegrationHealth`. There is deliberately no "configured, untested" state:
 * the BFF checks on the read, so every row is either answered, being checked,
 * or explains why it cannot be checked.
 */
export type IntegrationHealthState =
  | 'online'
  | 'offline'
  | 'checking'
  | 'simulated'
  | 'unverifiable'
  | 'no_credentials'
  | 'disabled';

/** How the state is known: a read-only probe, real traffic, or configuration. */
export type IntegrationHealthSource = 'probe' | 'traffic' | 'declared';

export interface IntegrationHealth {
  state: IntegrationHealthState;
  /** ISO-8601 instant of the evidence (probe ran / last call seen). */
  checkedAt?: string;
  /** Sanitized upstream: the failed step on `offline`. */
  detail?: string;
  source: IntegrationHealthSource;
}

/* ───────────────────── Operator on/off switch (#839) ──────────────────────
 * Mirrors `@nexa/integrations-contract`'s `IIntegrationToggleState`. The row's
 * `enabled` above is the EFFECTIVE state once an operator decided.
 */

/** Why the switch cannot be flipped right now. */
export type IntegrationToggleBlockReason = 'credentials_missing' | 'last_provider' | 'not_toggleable';

/** The operator decision in force — who, when, why. */
export interface IntegrationToggleChange {
  enabled: boolean;
  byUrn: string;
  byName?: string;
  at: string;
  reason?: string;
  /**
   * 2026-10-29 — capabilities this decision does NOT cover: it was taken before the provider
   * absorbed them (Uber's switch then was its trips', never its vouchers). Absent = all.
   */
  except?: string[];
}

export interface IntegrationToggle {
  allowed: boolean;
  reason?: IntegrationToggleBlockReason;
  lastChange?: IntegrationToggleChange;
}

/* ───────────────── Mode per binding (providers-by-service M2) ─────────────────
 * Mirrors the frozen B2 seam `operator-bff:POST /v1/providers/integrations/{vendor}/mode@v1`
 * and the `IIntegrationModeCommand@v1` refusals.
 */

/** The operator mode decision in force. `ignored`: chosen, but not served (§6 invariant 12). */
export interface IntegrationModeOverride {
  mode: IntegrationMode;
  byUrn: string;
  byName?: string;
  at: string;
  reason?: string;
  ignored?: boolean;
}

/** `IntegrationModeRefusal` — the 409 `{ reason }` of a mode write. */
export type IntegrationModeRefusal =
  | 'not_found'
  | 'not_mode_selectable'
  | 'mode_not_selectable'
  | 'mode_not_allowed'
  | 'reason_required'
  | 'conflict';

/** Why a mode write did not land: a 409 refusal, a 403, a 400, or anything else. */
export type IntegrationModeErrorCode = IntegrationModeRefusal | 'forbidden' | 'invalid_request' | 'unavailable';

/** `ws providers IntegrationChanged@v1` — emitted after a mutation that landed. */
export interface IntegrationChange {
  vendor: string;
  category: IntegrationCategory;
  change: 'mode' | 'toggle' | 'routing';
  at: string;
}

/** Why a toggle write was refused (409/404/400/503 from the BFF). */
export type IntegrationToggleErrorCode =
  | IntegrationToggleBlockReason
  | 'version_conflict'
  | 'unknown_vendor'
  | 'invalid_request'
  | 'unavailable';

/** Why a declaring app could not be asked. Never "not configured": this is
 *  exclusively about OUR failure to get an answer. */
export type UnknownDeclarantReason = 'unreachable' | 'timeout' | 'error';

/** "I could not ask `nexa-booking`, so I do not know what it holds." */
export interface UnknownDeclarant {
  declaredBy: string;
  reason: UnknownDeclarantReason;
  /** Sanitized: HTTP status / short error code only. */
  detail?: string;
  observedAt: string;
}

/** The aggregated answer to "what is this instance connected to?". */
export interface ProvidersIntegrations {
  integrations: IntegrationDescriptor[];
  /** Declarants we could not reach — NOT declarants that hold nothing. */
  unreachable: string[];
  /** Per-declarant reason behind `unreachable` (additive; present when sent). */
  unknown?: UnknownDeclarant[];
  computedAt: string;
}

/** One step of the probe chain. Mirrors `IIntegrationProbeStep`. */
export interface IntegrationProbeStep {
  step: string;
  ok: boolean;
  latencyMs: number;
  /** Sanitized upstream: HTTP status + short vendor message only. */
  detail?: string;
}

/** Result of one on-demand connection test. Mirrors `IIntegrationProbeResult`,
 *  which is shape-compatible with the shipped `IVendorTestResult`. */
export interface IntegrationProbeResult {
  vendor: string;
  env: string;
  /** Meaningless when `notProbeable` — read the verdict, never this flag. */
  ok: boolean;
  steps: IntegrationProbeStep[];
  testedAt: string;
  /** The vendor's profile declares no read-only endpoint we may call. NOT a
   *  failure, and it must never render as one. */
  notProbeable?: boolean;
}

/** A probe run either answered, or was refused by the per-vendor rate guard.
 *  A 429 is a friendly outcome, not an error. */
export type IntegrationProbeOutcome =
  | { kind: 'result'; result: IntegrationProbeResult }
  | { kind: 'rate_limited'; retryAfterMs?: number | undefined };

/* ───────────── Provider routing — "Orden de uso" (integrations-tds §14) ─────────────
 * Mirrors operator-bff's whitelisted `RoutingView` (`provider-routing.wire.ts`),
 * contract `integrations:provider-routing@v1`.
 */

/** How the providers of one routed key are combined. */
export type RoutingStrategy = 'SCATTER_GATHER' | 'PRIMARY_FALLBACK' | 'ROUND_ROBIN' | 'FIRST_SUCCESS';

/** One provider of a routed key. */
export interface RoutingMember {
  vendor: string;
  /** 1 = first. */
  priority: number;
  /** `ROUND_ROBIN` share, when one was stored. */
  weight?: number;
  /** "Participa" — false = parked (kept, never used). */
  enabled: boolean;
  /** Its integration is switched on in this environment right now. */
  serving: boolean;
  mode?: IntegrationMode;
}

/**
 * THE AIRLINE ALWAYS WINS (owner decision 2026-10-29, integrations-tds §14.1): the member whose
 * place the operator cannot change. It comes before every member of `over` (`'*'` = every other
 * source, so it is locked first) and takes part whenever one of them does; only its own switch
 * (the airline provider's card) takes it out. Present only when that vendor is a member.
 */
export interface RoutingLeader {
  vendor: string;
  over: '*' | string[];
  /** Why — the refusal reason a save that breaks the rule comes back with. */
  reason: string;
}

/** The decision for one `(category, capability)`. */
export interface RoutingView {
  category: IntegrationCategory;
  capability: string;
  strategy: RoutingStrategy;
  allowedStrategies: RoutingStrategy[];
  source: 'configured' | 'platform-default';
  /** The stored version — sent back as `expectedVersion`. Absent = nothing stored. */
  version?: number;
  updatedAt?: string;
  updatedByUrn?: string;
  members: RoutingMember[];
  leader?: RoutingLeader;
}

export interface ProvidersRouting {
  items: RoutingView[];
}

/** Body of `PUT /v1/providers/routing/:category/:capability`. */
export interface RoutingSaveBody {
  strategy: RoutingStrategy;
  members: Array<{ vendor: string; priority: number; weight?: number; enabled: boolean }>;
  expectedVersion: number;
  reason?: string;
}

/** Why a routing save was refused (400/409/403/503 from the BFF). */
export type RoutingSaveErrorCode = 'version_conflict' | 'invalid_request' | 'forbidden' | 'unavailable';

/** The domain's own reasons for a 400 the console names (else the generic `invalid_request`). */
export const ROUTING_REFUSAL_REASONS = ['airline_always_wins'] as const;
export type RoutingRefusalReason = (typeof ROUTING_REFUSAL_REASONS)[number];

/* ─────────────────────────── The airline provider ─────────────────────────── */

/** The services the airline provider is switched under (one integrations binding each). */
export type AirlineProviderService = 'flight-info' | 'passenger-data' | 'air-ticketing';

/** What the airline provider may give Nexa (`airline-air-ticketing.v1` §0). */
export type AirlineCapability =
  | 'flights'
  | 'signals'
  | 'manifests'
  | 'pnr'
  | 'remarks'
  | 'offers'
  | 'hold'
  | 'ticket'
  | 'release';

/** Why a capability is not available — the contract's four reasons. */
export type AirlineUnavailableReason =
  | 'not_connected'
  | 'switched_off'
  | 'capability_unsupported'
  | 'upstream_unavailable';

export interface AirlineCapabilityState {
  capability: AirlineCapability;
  service: AirlineProviderService;
  available: boolean;
  reason?: AirlineUnavailableReason;
  /**
   * The provider's data for this capability is GENERATED, not the airline's (a mocked service
   * adapter on a demo, `airline-service-provider.v1` «Synthetic data») — the card says «Sintético».
   */
  synthetic?: true;
}

/** Mirrors the operator-bff `IAirlineProviderView` (`GET /v1/providers/airline`). */
export interface AirlineProviderState {
  /** `false` = the BFF could not ask the airline: UNKNOWN, never "not connected". */
  reachable: boolean;
  connected: boolean;
  mode?: 'sandbox' | 'production';
  /** The operator's switch as the AIRLINE reads it (`null` = it could not read it; kept on). */
  switches: Partial<Record<AirlineProviderService, boolean | null>>;
  capabilities: AirlineCapabilityState[];
  checkedAt?: string;
  computedAt: string;
}

/** One service of a master-switch action (`POST /v1/providers/airline/switch`). */
export type AirlineServiceSwitchOutcome =
  | { service: AirlineProviderService; ok: true; row: IntegrationDescriptor }
  | { service: AirlineProviderService; ok: false; error: string };

export interface AirlineProviderSwitchResult {
  enabled: boolean;
  services: AirlineServiceSwitchOutcome[];
}
